import { Box, Container, Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import LandingPageSidebar from "./landPage/LandingPageSidebar";
import DNAModuleIcon from "../components/icons/module/DNAModuleIcon";
import EngagementModuleIcon from "../components/icons/module/EngagementModuleIcon";
import IntelligenceHubIcon from "../components/icons/module/IntelligenceHubIcon";
import LeadsModuleIcon from "../components/icons/module/LeadsModuleIcon";
import Omne360ModuleIcon from "../components/icons/module/Omne360ModuleIcon";
import RewardsModuleIcon from "../components/icons/module/RewardsModuleIcon";
import Header from "../components/organisms/Header";
import ModuleCard from "../components/organisms/ModuleCard";
import { rootPaths } from "../routes/routePaths";
import { useSetCampaignSearchStore } from "../stores/campaignSearchStore";
import { useSetCampaignStore } from "../stores/campaignStore";
import { useSetCustomerStore } from "../stores/customerStore";
import { useSetFilterStore } from "../stores/filterStore";
import { useSetSearchStore } from "../stores/formSearchStore";
import { usePermissionStore } from "../stores/permissionStore";
import { useSetSearchStore as setPpsSearchStore } from "../stores/ppsFormSearchStore";
import { useSetCustomerStore as setPpsStore } from "../stores/ppsStore";

export type Tool = {
  id: string;
  name: string;
  urlPath: string;
  permission?: boolean;
  imageSrc?: string;
  title?: string;
  description?: string;
  categories?: string[];
  icon?: React.ReactNode;
};

const Tools = () => {
  const {
    permissionCustomer,
    permissionInventory,
    permissionPPS,
    permissionLeads,
    permissionDNA,
    permissionIntelligenceHub,
    permissionEngage,
  } = usePermissionStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setFilterStore = useSetFilterStore();
  const setCustomerStore = useSetCustomerStore();
  const setCampaignStore = useSetCampaignStore();
  const setPps = setPpsStore();
  const setSearchStore = useSetSearchStore();
  const setPpsSearch = setPpsSearchStore();
  const setCampaignSearchStore = useSetCampaignSearchStore();
  const [selectedFilter, setSelectedFilter] = useState("Show all");

  const onFilterChange = (item: string) => {
    setSelectedFilter(item);
  };

  const handleButtonOnClick = (tool: Tool) => {
    switch (tool.urlPath) {
      case rootPaths.rewardInventoryPortal:
        {
          setFilterStore({
            productId: "",
            productName: "",
            productType: [],
            status: [],
            queryString: "",
          });
        }
        break;
      case rootPaths.customer360:
        {
          setCustomerStore({
            users: [],
            pagination: {
              currentPage: 1,
              pageSize: 12,
              totalPages: 0,
            },
          });
          setSearchStore({
            omneName: "",
            dob: "",
            phoneNumber: "",
            email: "",
            policyNumber: "",
            insuranceCountry: [],
            signUpCountry: [],
          });
        }
        break;
      case rootPaths.pps:
        {
          setPps({
            users: [],
            pagination: {
              currentPage: 1,
              pageSize: 12,
              totalPages: 0,
            },
          });
          setPpsSearch({
            omneId: "",
            signUpCountry: [],
          });
        }
        break;
      case rootPaths.leadsManagement:
        {
          setPps({
            users: [],
            pagination: {
              currentPage: 1,
              pageSize: 12,
              totalPages: 0,
            },
          });
          setPpsSearch({
            omneId: "",
            signUpCountry: [],
          });
        }
        break;
      case rootPaths.campaign:
        {
          setCampaignStore({
            campaignDetails: [],
            pagination: {
              currentPage: 1,
              pageSize: 12,
              totalPages: 0,
            },
          });
          setCampaignSearchStore({
            campaignName: "",
            campaignID: "",
            startDate: "",
            endDate: "",
            status: "",
          });
        }
        break;
    }
    navigate(tool.urlPath);
  };

  const availableTools: Tool[] = [
    {
      id: "intelligence-hub-portal",
      name: "Intelligence Hub Portal",
      urlPath: rootPaths.intelligenceHub,
      permission: permissionIntelligenceHub,
      imageSrc: "/assets/images/intelligenceHub.png",
      title: t("platform.LandingPage_intelligenceHubTitle"),
      description: t("platform.LandingPage_intelligenceHubDescription"),
      categories: ["Business intelligence"],
      icon: <IntelligenceHubIcon />,
    },
    {
      id: "leads-management",
      name: "Leads Management Portal",
      urlPath: rootPaths.leadsManagement,
      permission: permissionLeads,
      imageSrc:
        "/assets/images/FWD_AI_generated_image_-_Confident_Businesswoman_in_Bright_Office_Space.png",
      title: t("platform.LandingPage_bridgeForLeadsTitle"),
      description: t("platform.LandingPage_bridgeForLeadsDescription"),
      categories: ["Sales"],
      icon: <LeadsModuleIcon />,
    },
    {
      id: "reward-inventory-portal",
      name: "Reward Inventory Portal",
      urlPath: rootPaths.rewardInventoryPortal,
      permission: permissionInventory,
      imageSrc:
        "/assets/images/FWD_employee_-_A_lady_coming_down_the_stairway_with_a_huge_gift.png",
      title: t("platform.LandingPage_bridgeRewardsTitle"),
      description: t("platform.LandingPage_bridgeRewardsDescription"),
      categories: ["Rewards and Recognition"],
      icon: <RewardsModuleIcon />,
    },
    // {
    //   id: "bridge-engage-portal",
    //   name: "Bridge Engage Portal",
    //   urlPath: "/",
    //   permission: permissionEngage,
    //   imageSrc: "/assets/images/intelligenceHub.png",
    //   title: t("platform.LandingPage_bridgeEngageTitle"),
    //   description: t("platform.LandingPage_bridgeEngageDescription"),
    //   categories: ["Marketing"],
    //   icon: <EngagementModuleIcon />,
    // },
    {
      id: "bridge-engage-portal",
      name: "Bridge Engage Portal",
      urlPath: rootPaths.campaign,
      permission: permissionEngage,
      imageSrc: "/assets/images/campaign.png",
      title: t("platform.LandingPage_bridgeEngageTitle"),
      description: t("platform.LandingPage_bridgeEngageDescription"),
      categories: ["Marketing"],
      icon: <EngagementModuleIcon />,
    },
    // this module is missing in figma
    {
      id: "PPS",
      name: "Omne PPS Portal",
      urlPath: rootPaths.pps,
      permission: permissionPPS,
      imageSrc: "/assets/images/intelligenceHub.png",
      title: t("platform.LandingPage_omne360Title"),
      description: t("platform.LandingPage_omne360Description"),
      categories: ["User Management"],
      icon: <DNAModuleIcon />,
    },
    {
      id: "customer-360",
      name: "Omne Customer 360 Portal",
      urlPath: rootPaths.customer360,
      permission: permissionCustomer,
      imageSrc: "/assets/images/intelligenceHub.png",
      title: t("platform.LandingPage_omne360Title"),
      description: t("platform.LandingPage_omne360Description"),
      categories: ["User Management"],
      icon: <Omne360ModuleIcon />,
    },
    {
      id: "DNA",
      name: "Omne DNA Portal",
      urlPath: rootPaths.dna,
      permission: permissionDNA,
      imageSrc: "/assets/images/bridgeForLeads.png",
      title: t("platform.LandingPage_omneDnaTitle"),
      description: t("platform.LandingPage_omneDnaDescription"),
      categories: ["Analytics"],
      icon: <DNAModuleIcon />,
    },
  ];

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        background: `
        linear-gradient(to right, 
          rgba(232, 119, 34, 0.9) 0%, 
          rgba(232, 119, 34, 0.7) 15%, 
          rgba(232, 119, 34, 0.3) 30%, 
          transparent 50%
        ),
        url("/assets/images/backgroundImage.png")
      `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Stack
        direction="column"
        sx={{
          width: { xs: "90%", sm: "90%", md: "90%", lg: "90%", xl: "90%" },
          margin: "0 auto",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Header title="Tools Index" />
        <Stack
          direction="row"
          sx={{
            overflow: "hidden",
            backgroundColor: "white",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            width: "100%",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              minWidth: "200px",
              background: "#FAE4D3",
              height: "100%",
            }}
          >
            <LandingPageSidebar
              onFilterChange={onFilterChange}
              selectedFilter={selectedFilter}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Container
              maxWidth={false}
              sx={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 2,
                padding: "36px",
                overflowY: "auto",
                flex: 1,
                height: "100%",
                alignContent: "flex-start",
                maxWidth: { xs: "100%", lg: "1800px" },
              }}
            >
              <ModuleCard
                items={availableTools}
                onClick={handleButtonOnClick}
                selectedFilter={selectedFilter}
              />
            </Container>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Tools;
